// Import media
import tokenLoopBannerImg from './img/token-loop-banner.svg';

// Import dependencies
import { html, render, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-loop-add-token-loop' )
export class AutomatorBuilderLoopAddTokenLoop extends LitElement {
	// The parent item ID (post ID)
	@property( { type: Number, attribute: 'parent-id' } ) accessor parentID;

	@property( { type: String } ) accessor size;

	@property( { type: String } ) accessor color = 'secondary';

	// eslint-disable-next-line require-jsdoc
	render() {
		// If the button shouldn't render, return nothing
		if ( ! this.shouldRender() ) {
			return;
		}

		return html`
			<uo-button
				size=${ this.size }
				color=${ this.color }
				@click=${ this.handleClick }
			>
				${ this._templateLabel }
			</uo-button>
		`;
	}

	/**
	 * Returns the template for the label
	 *
	 * @return {TemplateResult} The template for the label
	 */
	get _templateLabel() {
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return html`${ escapeHTML( __( 'Token loop', 'uncanny-automator' ) ) }`;
		}

		return html`
			${ escapeHTML( __( 'Token loop', 'uncanny-automator' ) ) } <uo-pro-tag size=${ ifDefined( this.size ) }></uo-pro-tag>
		`;
	}

	/**
	 * Handles the click event on the button.
	 *
	 * This method is responsible for determining what to do when the user clicks the button.
	 * — If the user is not a Pro user, it will show the upsell dialog.
	 * — If the user is a Pro user, it will add a token loop to the recipe.
	 *
	 * @return {undefined}
	 */
	handleClick() {
		// If the user doesn't have Pro, show the upsell dialog
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			this.handleCreateUpsellDialog();
			return;
		}

		// Otherwise, add the user loop
		this.handleAddTokenLoop();
	}

	/**
	 * Handles adding a loop too the recipe
	 */
	handleAddTokenLoop() {
		// Select the token
		this.createEditFieldsDialog();
	}

	/**
	 * Creates the dialog that lets the user edit the fields of the loop
	 */
	createEditFieldsDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uap-builder-item-loop-edit-dialog 
					parent-id=${ this.parentID }
					type="token"
				></uap-builder-item-loop-edit-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Creates the dialog that explains to non-Pro users what this feature does and how they can get it.
	 */
	handleCreateUpsellDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ escapeHTML( __( 'Token loop', 'uncanny-automator' ) ) }
					featured-image=${ tokenLoopBannerImg }
					featured-image-position="center bottom"
					size="large"
					open
				>
					<p>
						<uo-markdown 
							allowed-tags="em"
							.content=${ escapeHTML( __( 'Token loops let you perform bulk actions on special *loopable tokens*. Here are a few examples:', 'uncanny-automator' ) ) }>
						</uo-markdown>
					</p>

					<ul>
						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'When a user purchases a specific product, **loop over their previously purchased products** and if a complementary product is found, send an email with special instructions or a coupon code.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Every Friday, **loop over all orders in the past week** and export them to a Google sheet.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'When a user completes a course, **loop over their previously completed courses** and if a corequisite course is found, enroll the user in a new course or issue a certificate of completion.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'When a user purchases a subscription, **loop over their active subscriptions** and if a complementary subscription is found, award an achievement or store credit.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>
					</ul>
					
					<uo-button
						slot="primary-action"

						href=${ this.upsaleURL }
						target="_blank"

						dialog-action="close"
					>
						${ escapeHTML( __( 'Get Token loops', 'uncanny-automator' ) ) }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * The URL to the upsell page. This URL will include UTM parameters to track the user's journey.
	 *
	 * @return {String} The URL to the upsell page.
	 */
	get upsaleURL() {
		return addParametersToURL(
			// Add UTM parameters
			addParametersUTM( 'https://automatorplugin.com/pricing/', {
				medium: 'recipe_creator',
				content: 'add_token_loop_button',
			} ),
			// Add context to show banner in the pricing page
			{
				uap_bpro_context: 'token_loop'
			}
		)
	}

	/**
	 * Determines whether or not the button should render
	 *
	 * @return {Boolean} Whether or not the button should render
	 */
	shouldRender() {
		// If the user doesn't have Pro, render it because we're going to upsell the feature
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return true;
		}

		return true;
	}
}
